import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from './useAdminAuth';

interface AdminPermission {
  permission_key: string;
  resource_scope: string;
  expires_at: string | null;
}

export function useAdminPermissions() {
  const { user, isAdmin, loading: authLoading } = useAdminAuth();
  const [permissions, setPermissions] = useState<AdminPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  // `initialized` flips true only after a real RPC fetch completes (not the
  // short-circuit !isAdmin path). Gates use this to avoid flashing a "no
  // access" screen while admin role flags are still resolving.
  const [initialized, setInitialized] = useState(false);

  const fetchPermissions = useCallback(async () => {
    if (!user?.id || !isAdmin) {
      setPermissions([]);
      setIsSuperAdmin(false);
      setLoading(false);
      // Intentionally do NOT set initialized=true here — auth may still be
      // mid-resolution and isAdmin could flip true on the next tick.
      return;
    }

    try {
      const [{ data, error }, { data: superFlag }] = await Promise.all([
        supabase.rpc('get_admin_permissions', { p_user_id: user.id }),
        // is_admin_super = explicit '*' grant in admin_permissions OR one of
        // the three HIBANE founders (info/kehinde/himoru). All other admins —
        // including @hibane.ai staff — are governed strictly by their
        // granted admin_permissions rows.
        supabase.rpc('is_admin_super' as never, { _user_id: user.id } as never),
      ]);

      if (error) {
        console.error('Error fetching admin permissions:', error);
        setPermissions([]);
        setIsSuperAdmin(Boolean(superFlag));
      } else {
        setPermissions(data || []);
        const hasStar = data?.some((p: AdminPermission) => p.permission_key === '*') || false;
        setIsSuperAdmin(hasStar || Boolean(superFlag));
      }
      setInitialized(true);
    } catch (err) {
      console.error('Exception fetching permissions:', err);
      setPermissions([]);
      setIsSuperAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [user?.id, isAdmin]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  /**
   * Check if the current admin has a specific permission
   * @param permission The permission key to check (e.g., 'members.read', 'blog.write')
   * @param resourceId Optional specific resource ID to check scope against
   */
  const hasPermission = useCallback((permission: string, resourceId?: string): boolean => {
    // Super admin has all permissions
    if (isSuperAdmin) return true;

    // Check for exact permission match
    const hasExact = permissions.some(p => 
      p.permission_key === permission && 
      (p.resource_scope === '*' || p.resource_scope === resourceId)
    );
    if (hasExact) return true;

    // Check for category wildcard (e.g., 'members.*')
    const category = permission.split('.')[0];
    const hasWildcard = permissions.some(p => 
      p.permission_key === `${category}.*` &&
      (p.resource_scope === '*' || p.resource_scope === resourceId)
    );
    
    return hasWildcard;
  }, [permissions, isSuperAdmin]);

  /**
   * Check if admin has any of the given permissions
   */
  const hasAnyPermission = useCallback((permissionList: string[]): boolean => {
    if (isSuperAdmin) return true;
    return permissionList.some(p => hasPermission(p));
  }, [hasPermission, isSuperAdmin]);

  /**
   * Check if admin has all of the given permissions
   */
  const hasAllPermissions = useCallback((permissionList: string[]): boolean => {
    if (isSuperAdmin) return true;
    return permissionList.every(p => hasPermission(p));
  }, [hasPermission, isSuperAdmin]);

  /**
   * Get all permissions for a specific category
   */
  const getCategoryPermissions = useCallback((category: string): string[] => {
    if (isSuperAdmin) {
      // Return all possible permissions for the category
      return permissions
        .filter(p => p.permission_key.startsWith(`${category}.`))
        .map(p => p.permission_key);
    }
    
    return permissions
      .filter(p => p.permission_key.startsWith(`${category}.`) || p.permission_key === `${category}.*`)
      .map(p => p.permission_key);
  }, [permissions, isSuperAdmin]);

  return {
    permissions,
    loading: loading || authLoading,
    initialized,
    isSuperAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getCategoryPermissions,
    refetch: fetchPermissions,
  };
}

export default useAdminPermissions;
